import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Image as ImageIcon, Download, Send, RefreshCw, CheckCircle2, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, ChevronUp, X, ShoppingBag, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/api/apiClient';
import OptimizedImage from '@/components/ui/OptimizedImage';

// Cast untyped UI components to any to avoid TS errors in TSX
const TabsAny = Tabs as any;
const TabsContentAny = TabsContent as any;
const TabsListAny = TabsList as any;
const TabsTriggerAny = TabsTrigger as any;
const InputAny = Input as any;
const LabelAny = Label as any;

interface BrandPosterData {
    id: number;
    name: string;
    slug: string;
    logo_url: string;
    description: string;
    candidates: {
        id: number;
        name: string;
        image: string;
    }[];
}

interface BrandProduct {
    id: number;
    name: string;
    images: string[];
    price: number;
    original_price?: number;
    is_new?: boolean;
}

const ITEMS_PER_PAGE = 12;
const MAX_SELECTIONS = 3;

// ─── Brand Product Picker Modal ─────────────────────────────────────────────
// Full-screen modal reusing the existing brand page API to load ALL products
const BrandProductPickerModal = ({
    brand,
    selectedPhotos,
    previews,
    generating,
    localDescription,
    showNextArrow,
    onTogglePhoto,
    onGeneratePoster,
    onDownload,
    onDescriptionChange,
    onClose,
}: {
    brand: BrandPosterData;
    selectedPhotos: number[];
    previews: string | undefined;
    generating: number | 'header' | 'all' | null;
    localDescription: string | undefined;
    showNextArrow: boolean;
    onTogglePhoto: (brandId: number, productId: number) => void;
    onGeneratePoster: (brand: BrandPosterData) => void;
    onDownload: (url: string, filename: string) => void;
    onDescriptionChange: (brandId: number, value: string) => void;
    onClose: () => void;
}) => {
    const [products, setProducts] = useState<BrandProduct[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const observerRef = useRef<IntersectionObserver | null>(null);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const isGenerating = generating === brand.id;
    const selectedCount = selectedPhotos?.length || 0;

    // Fetch products from the brand page API
    const fetchProducts = useCallback(async (pageNum: number, reset: boolean = false) => {
        try {
            if (reset) setLoading(true);
            const data = await (apiClient as any).brands.getBySlug(brand.slug, {
                tab: 'all',
                page: pageNum,
                limit: 24,
            });
            if (data && data.products) {
                if (reset || pageNum === 1) {
                    setProducts(data.products);
                } else {
                    setProducts(prev => {
                        const existingIds = new Set(prev.map((p: BrandProduct) => p.id));
                        const newOnes = data.products.filter((p: BrandProduct) => !existingIds.has(p.id));
                        return [...prev, ...newOnes];
                    });
                }
                setHasMore(data.products.length >= 24);
                setTotalCount(data.totalCount || 0);
            }
        } catch (error) {
            toast.error('Erreur lors du chargement des produits');
        } finally {
            setLoading(false);
        }
    }, [brand.slug]);

    // Initial load
    useEffect(() => {
        setPage(1);
        setProducts([]);
        setHasMore(true);
        fetchProducts(1, true);
    }, [fetchProducts]);

    // Infinite scroll observer
    const handleObserver = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries;
            if (entry.isIntersecting && hasMore && !loading) {
                setPage(prev => prev + 1);
            }
        },
        [hasMore, loading]
    );

    useEffect(() => {
        if (observerRef.current) observerRef.current.disconnect();
        observerRef.current = new IntersectionObserver(handleObserver, { threshold: 0.1 });
        if (loadMoreRef.current) observerRef.current.observe(loadMoreRef.current);
        return () => observerRef.current?.disconnect();
    }, [handleObserver]);

    // Fetch next page
    useEffect(() => {
        if (page > 1) {
            fetchProducts(page);
        }
    }, [page, fetchProducts]);

    // Filter products by search query (client-side filtering of loaded products)
    const displayedProducts = useMemo(() => {
        if (!searchQuery.trim()) return products;
        const q = searchQuery.toLowerCase().trim();
        return products.filter(p => p.name?.toLowerCase().includes(q));
    }, [products, searchQuery]);

    const handleProductClick = (productId: number) => {
        onTogglePhoto(brand.id, productId);
    };

    // Close on Escape
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    // Prevent body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    return createPortal(
        <div className="fixed inset-0 z-[100] flex flex-col">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

            {/* Modal Panel */}
            <div className="relative z-[101] flex flex-col h-full max-h-screen bg-white dark:bg-gray-950 animate-in fade-in slide-in-from-bottom-4 duration-300">
                {/* ─── Sticky Header ──────────────────────────────────────── */}
                <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                                <button
                                    onClick={onClose}
                                    className="flex-shrink-0 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                >
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                                {brand.logo_url ? (
                                    <img
                                        src={brand.logo_url}
                                        alt=""
                                        className="w-10 h-10 object-contain rounded bg-white p-1 border flex-shrink-0"
                                        onError={(e: any) => e.target.style.display = 'none'}
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/50 dark:to-yellow-900/50 flex items-center justify-center flex-shrink-0">
                                        <span className="text-lg font-bold text-amber-600 dark:text-amber-400">{brand.name?.charAt(0)}</span>
                                    </div>
                                )}
                                <div className="min-w-0">
                                    <h2 className="text-xl font-bold text-gray-900 dark:text-white truncate">{brand.name}</h2>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {totalCount} produit{totalCount > 1 ? 's' : ''} · Sélection : {selectedCount}/{MAX_SELECTIONS}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {previews && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onDownload(previews, `pepite_${brand.slug}.png`)}
                                        className="text-green-600 hover:text-green-700"
                                    >
                                        <Download className="h-4 w-4 mr-1.5" /> Télécharger
                                    </Button>
                                )}
                                <Button
                                    size="sm"
                                    className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-sm"
                                    onClick={() => onGeneratePoster(brand)}
                                    disabled={isGenerating || selectedCount === 0}
                                >
                                    {isGenerating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ImageIcon className="h-4 w-4 mr-1.5" />}
                                    Générer l'affiche ({selectedCount})
                                </Button>
                            </div>
                        </div>

                        {/* Search + Description (collapsible) */}
                        <div className="mt-3 flex flex-col sm:flex-row gap-3">
                            {/* Search */}
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Filtrer les produits par nom…"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-10 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                            {/* Description for the poster */}
                            <div className="flex-1">
                                <input
                                    type="text"
                                    value={localDescription !== undefined ? localDescription : (brand.description || "")}
                                    onChange={(e) => onDescriptionChange(brand.id, e.target.value)}
                                    placeholder="Description pour l'affiche…"
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all"
                                />
                            </div>
                        </div>

                        {/* Selected products preview chips */}
                        {selectedCount > 0 && (
                            <div className="mt-3 flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sélection :</span>
                                {products.filter(p => selectedPhotos.includes(p.id)).map(p => (
                                    <button
                                        key={p.id}
                                        onClick={() => handleProductClick(p.id)}
                                        className="flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 text-xs font-medium hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors group"
                                    >
                                        <img src={p.images?.[0]} alt="" className="w-5 h-5 rounded-full object-cover" />
                                        <span className="max-w-[120px] truncate">{p.name}</span>
                                        <X className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                                    </button>
                                ))}
                                {/* Also show candidates that are selected but not yet loaded in products */}
                                {brand.candidates
                                    .filter(c => selectedPhotos.includes(c.id) && !products.some(p => p.id === c.id))
                                    .map(c => (
                                        <button
                                            key={c.id}
                                            onClick={() => handleProductClick(c.id)}
                                            className="flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 text-xs font-medium hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors group"
                                        >
                                            <img src={c.image} alt="" className="w-5 h-5 rounded-full object-cover" />
                                            <span className="max-w-[120px] truncate">{c.name}</span>
                                            <X className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* ─── Scrollable Product Grid ────────────────────────────── */}
                <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
                        {/* Preview (if generated) */}
                        {previews && (
                            <div className="mb-6 max-w-md mx-auto animate-in fade-in zoom-in duration-300">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 text-center">Preview de l'affiche</p>
                                <img src={previews} alt="Preview" className="w-full rounded-xl shadow-2xl border border-gray-100" />
                            </div>
                        )}

                        {/* Loading state */}
                        {loading && products.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-20 gap-3">
                                <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                                <p className="text-sm text-gray-500">Chargement des produits…</p>
                            </div>
                        )}

                        {/* Empty state */}
                        {!loading && displayedProducts.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                                <ShoppingBag className="h-12 w-12 text-gray-300" />
                                <p className="text-lg font-medium text-gray-500">
                                    {searchQuery ? 'Aucun produit correspondant' : 'Aucun produit pour cette marque'}
                                </p>
                                {searchQuery && (
                                    <Button variant="ghost" size="sm" onClick={() => setSearchQuery('')}>
                                        Réinitialiser la recherche
                                    </Button>
                                )}
                            </div>
                        )}

                        {/* Product Grid (selectable) */}
                        {displayedProducts.length > 0 && (
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                                {displayedProducts.map(product => {
                                    const isSelected = selectedPhotos.includes(product.id);
                                    const imageUrl = product.images?.[0];
                                    return (
                                        <button
                                            key={product.id}
                                            onClick={() => handleProductClick(product.id)}
                                            className={`group relative rounded-xl overflow-hidden border-2 transition-all duration-200 text-left ${
                                                isSelected
                                                    ? 'border-amber-600 ring-2 ring-amber-200 dark:ring-amber-900 shadow-lg scale-[0.97]'
                                                    : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md'
                                            }`}
                                        >
                                            {/* Image */}
                                            <div className="aspect-[3/4] bg-gray-100 dark:bg-gray-800 overflow-hidden">
                                                {imageUrl ? (
                                                    <img
                                                        src={imageUrl}
                                                        alt={product.name}
                                                        className={`w-full h-full object-cover transition-all duration-200 ${
                                                            isSelected ? 'brightness-90' : 'group-hover:scale-105'
                                                        }`}
                                                        loading="lazy"
                                                        onError={(e: any) => e.target.src = 'https://placehold.co/200x260?text=No+Image'}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <ImageIcon className="w-8 h-8 text-gray-300" />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Selection overlay */}
                                            {isSelected && (
                                                <div className="absolute top-2 right-2 bg-amber-500 text-white rounded-full p-1 shadow-lg animate-in zoom-in duration-150">
                                                    <CheckCircle2 className="w-4 h-4" />
                                                </div>
                                            )}

                                            {/* Hover overlay for non-selected */}
                                            {!isSelected && selectedCount < MAX_SELECTIONS && (
                                                <div className="absolute inset-0 bg-amber-500/0 group-hover:bg-amber-500/10 transition-colors flex items-center justify-center">
                                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 dark:bg-gray-900/90 rounded-full p-1.5 shadow-md">
                                                        <CheckCircle2 className="w-4 h-4 text-amber-500" />
                                                    </div>
                                                </div>
                                            )}

                                            {/* Dimmed overlay when max reached and not selected */}
                                            {!isSelected && selectedCount >= MAX_SELECTIONS && (
                                                <div className="absolute inset-0 bg-gray-900/30" />
                                            )}

                                            {/* Product info */}
                                            <div className="p-2">
                                                <p className="text-xs font-medium text-gray-900 dark:text-white line-clamp-1">{product.name}</p>
                                                <div className="flex items-center gap-1 mt-0.5">
                                                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{product.price}€</span>
                                                    {product.original_price && product.original_price > product.price && (
                                                        <span className="text-[10px] text-gray-400 line-through">{product.original_price}€</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* New badge */}
                                            {product.is_new && (
                                                <div className="absolute top-2 left-2">
                                                    <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm flex items-center gap-0.5">
                                                        <Sparkles className="w-2.5 h-2.5" /> New
                                                    </span>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Infinite scroll trigger */}
                        <div ref={loadMoreRef} className="h-16 flex items-center justify-center">
                            {loading && products.length > 0 && (
                                <div className="w-8 h-8 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
                            )}
                            {!hasMore && products.length > 0 && (
                                <p className="text-xs text-gray-400">Tous les produits sont affichés ✨</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

// ─── Simplified Brand Card ──────────────────────────────────────────────────
// Now a simple clickable card that shows the brand + selected product thumbnails
const BrandCard = React.memo(({
    brand,
    selectedPhotos,
    previews,
    generating,
    onSelectBrand,
}: {
    brand: BrandPosterData;
    selectedPhotos: number[];
    previews: string | undefined;
    generating: number | 'header' | 'all' | null;
    onSelectBrand: (brand: BrandPosterData) => void;
}) => {
    const isGenerating = generating === brand.id;
    const selectedCount = selectedPhotos?.length || 0;

    // Get thumbnails for selected products from candidates
    const selectedThumbnails = brand.candidates
        .filter(c => selectedPhotos?.includes(c.id))
        .slice(0, MAX_SELECTIONS);

    return (
        <Card
            className="overflow-hidden flex flex-col border-gray-200 hover:border-amber-400 hover:shadow-lg transition-all cursor-pointer group"
            onClick={() => onSelectBrand(brand)}
        >
            <CardHeader className="bg-gray-50/50 dark:bg-gray-900/50 pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                        {brand.logo_url ? (
                            <img
                                src={brand.logo_url}
                                alt=""
                                className="w-10 h-10 object-contain rounded bg-white p-1 border flex-shrink-0"
                                onError={(e: any) => e.target.style.display = 'none'}
                            />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/50 dark:to-yellow-900/50 flex items-center justify-center text-amber-600 dark:text-amber-400 font-bold text-lg flex-shrink-0">
                                {brand.name?.charAt(0)}
                            </div>
                        )}
                        <div className="min-w-0">
                            <CardTitle className="text-base font-semibold truncate group-hover:text-amber-600 transition-colors">{brand.name}</CardTitle>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {brand.candidates.length} produit{brand.candidates.length > 1 ? 's' : ''}
                                {selectedCount > 0 && (
                                    <span className="text-amber-600 font-medium"> · {selectedCount} sélectionné{selectedCount > 1 ? 's' : ''}</span>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {isGenerating && (
                            <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                        )}
                        {previews && !isGenerating && (
                            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium px-2 py-0.5 rounded-full">
                                ✓ Généré
                            </span>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all" />
                    </div>
                </div>
            </CardHeader>

            {/* Selected products thumbnails */}
            {selectedThumbnails.length > 0 && (
                <CardContent className="p-3 pt-0">
                    <div className="flex gap-1.5 mt-2">
                        {selectedThumbnails.map(prod => (
                            <div
                                key={prod.id}
                                className="w-12 h-12 rounded-md overflow-hidden border-2 border-amber-200 dark:border-amber-800 flex-shrink-0"
                            >
                                <img
                                    src={prod.image}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    onError={(e: any) => e.target.src = 'https://placehold.co/48x48?text=?'}
                                />
                            </div>
                        ))}
                        {selectedCount > selectedThumbnails.length && (
                            <div className="w-12 h-12 rounded-md border-2 border-dashed border-amber-200 dark:border-amber-800 flex items-center justify-center text-xs text-amber-600 font-medium flex-shrink-0">
                                +{selectedCount - selectedThumbnails.length}
                            </div>
                        )}
                        {Array.from({ length: MAX_SELECTIONS - Math.max(selectedCount, selectedThumbnails.length) }).map((_, i) => (
                            <div
                                key={`empty-${i}`}
                                className="w-12 h-12 rounded-md border-2 border-dashed border-gray-200 dark:border-gray-700 flex items-center justify-center flex-shrink-0"
                            >
                                <ImageIcon className="w-4 h-4 text-gray-300" />
                            </div>
                        ))}
                    </div>
                </CardContent>
            )}

            {/* Preview thumbnail */}
            {previews && (
                <div className="px-3 pb-3">
                    <img src={previews} alt="Preview" className="w-full h-20 object-cover rounded-md border border-gray-100 dark:border-gray-800 opacity-80" />
                </div>
            )}
        </Card>
    );
});

BrandCard.displayName = 'BrandCard';


// ─── Pagination Component ───────────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, onPageChange }: {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}) => {
    if (totalPages <= 1) return null;

    // Build visible page numbers (show max 7 pages with ellipsis)
    const getVisiblePages = (): (number | '...')[] => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        const pages: (number | '...')[] = [1];
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);

        if (start > 2) pages.push('...');
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < totalPages - 1) pages.push('...');
        pages.push(totalPages);

        return pages;
    };

    return (
        <div className="flex items-center justify-center gap-1 mt-8 select-none">
            <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                className="h-9 w-9 p-0"
                title="Première page"
            >
                <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="h-9 w-9 p-0"
                title="Page précédente"
            >
                <ChevronLeft className="h-4 w-4" />
            </Button>

            {getVisiblePages().map((page, i) => (
                page === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-gray-400 text-sm">…</span>
                ) : (
                    <Button
                        key={page}
                        variant={page === currentPage ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => onPageChange(page)}
                        className={`h-9 w-9 p-0 text-sm font-medium ${
                            page === currentPage
                                ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        {page}
                    </Button>
                )
            ))}

            <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="h-9 w-9 p-0"
                title="Page suivante"
            >
                <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="h-9 w-9 p-0"
                title="Dernière page"
            >
                <ChevronsRight className="h-4 w-4" />
            </Button>
        </div>
    );
};


// ─── Main PosterStudio Component ────────────────────────────────────────────
const PosterStudio: React.FC = () => {
    const [brands, setBrands] = useState<BrandPosterData[]>([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState<number | 'header' | 'all' | null>(null);
    const [selectedPhotos, setSelectedPhotos] = useState<Record<number, number[]>>({});
    const [previews, setPreviews] = useState<Record<string, string>>({});
    const [headerPart, setHeaderPart] = useState('Partie 2');
    const [showNextArrow, setShowNextArrow] = useState(false);
    const [localDescriptions, setLocalDescriptions] = useState<Record<number, string>>({});

    // Modal state
    const [activeBrand, setActiveBrand] = useState<BrandPosterData | null>(null);

    // ─── Search & Pagination state ──────────────────────────────────────────
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await apiClient.admin.getPosterData();
            if (response.success) {
                setBrands(response.data);
                // Initialize selection with first 2 candidates for each brand
                const initialSelection: Record<number, number[]> = {};
                response.data.forEach((b: BrandPosterData) => {
                    initialSelection[b.id] = b.candidates.slice(0, 2).map(c => c.id);
                });
                setSelectedPhotos(initialSelection);
            }
        } catch (error) {
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    // ─── Filtered & paginated brands ────────────────────────────────────────
    const filteredBrands = useMemo(() => {
        if (!searchQuery.trim()) return brands;
        const q = searchQuery.toLowerCase().trim();
        return brands.filter(b =>
            b.name.toLowerCase().includes(q) ||
            b.slug.toLowerCase().includes(q)
        );
    }, [brands, searchQuery]);

    const totalPages = Math.max(1, Math.ceil(filteredBrands.length / ITEMS_PER_PAGE));

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    // Clamp current page if filtered results shrink
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    const paginatedBrands = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredBrands.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredBrands, currentPage]);

    // ─── Callbacks (stable references) ──────────────────────────────────────
    const togglePhoto = useCallback((brandId: number, productId: number) => {
        setSelectedPhotos(prev => {
            const current = prev[brandId] || [];
            if (current.includes(productId)) {
                return { ...prev, [brandId]: current.filter(id => id !== productId) };
            } else {
                if (current.length >= MAX_SELECTIONS) {
                    toast.warning(`Maximum ${MAX_SELECTIONS} photos par affiche`);
                    return prev;
                }
                return { ...prev, [brandId]: [...current, productId] };
            }
        });
    }, []);

    const generateHeader = useCallback(async () => {
        try {
            setGenerating('header');
            const blob = await apiClient.admin.generateHeaderPoster(headerPart || 1);
            const url = URL.createObjectURL(blob);
            setPreviews(prev => ({ ...prev, header: url }));
            toast.success('Affiche Header générée !');
        } catch (error) {
            toast.error('Erreur de génération');
        } finally {
            setGenerating(null);
        }
    }, [headerPart]);

    const generateBrandPoster = useCallback(async (brand: BrandPosterData) => {
        try {
            setGenerating(brand.id);
            const productIds = selectedPhotos[brand.id] || [];
            if (productIds.length === 0) {
                toast.error('Veuillez sélectionner au moins une photo');
                setGenerating(null);
                return;
            }

            const customDesc = localDescriptions[brand.id] || null;
            const blob = await (apiClient.admin as any).generateBrandPoster(brand.slug, productIds, showNextArrow, customDesc);
            const url = URL.createObjectURL(blob);
            setPreviews(prev => ({ ...prev, [brand.id]: url }));
            toast.success(`Affiche pour ${brand.name} générée !`);
        } catch (error) {
            toast.error('Erreur de génération');
        } finally {
            setGenerating(null);
        }
    }, [selectedPhotos, localDescriptions, showNextArrow]);

    const downloadImage = useCallback((url: string, filename: string) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, []);

    const handleDescriptionChange = useCallback((brandId: number, value: string) => {
        setLocalDescriptions(prev => ({ ...prev, [brandId]: value }));
    }, []);

    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page);
        // Scroll to top of the brands list
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleSelectBrand = useCallback((brand: BrandPosterData) => {
        setActiveBrand(brand);
    }, []);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                <Loader2 className="w-10 h-10 animate-spin text-amber-600" />
                <p className="text-muted-foreground font-medium">Préparation du studio...</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-8 px-4 max-w-7xl">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div className="flex flex-col gap-2">
                    <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">Pépite Poster Studio</h1>
                    <p className="text-lg text-muted-foreground">Générez vos visuels de communication en un clic.</p>
                </div>
                <Button
                    onClick={async () => {
                        if (!confirm('Générer toutes les marques d\'un coup ? Cela peut prendre quelques minutes.')) return;
                        setGenerating('all');
                        try {
                            const response = await apiClient.admin.generateAllPosters();
                            if (response.success) {
                                toast.success('Génération terminée ! Les fichiers sont dans public/generated_posters');
                            }
                        } catch (e) {
                            toast.error('Erreur lors de la génération de masse');
                        } finally {
                            setGenerating(null);
                        }
                    }}
                    disabled={generating !== null}
                    className="bg-black hover:bg-gray-800 text-white"
                >
                    {generating === 'all' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageIcon className="mr-2 h-4 w-4" />}
                    Générer toutes les marques
                </Button>
            </div>

            {/* Global Options */}
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900 rounded-xl p-4 mb-8 flex items-center justify-between">
                <div className="flex flex-col gap-1">
                    <h3 className="font-semibold text-amber-900 dark:text-amber-100">Options globales d'affichage</h3>
                    <p className="text-sm text-amber-700 dark:text-amber-300">Ces options s'appliquent lors de la génération individuelle.</p>
                </div>
                <div className="flex items-center gap-3 bg-white dark:bg-gray-900 px-4 py-2 rounded-lg border shadow-sm">
                    <input
                        type="checkbox"
                        id="showNextArrow"
                        checked={showNextArrow}
                        onChange={(e) => setShowNextArrow(e.target.checked)}
                        className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                    />
                    <LabelAny htmlFor="showNextArrow" className="font-medium cursor-pointer">
                        Afficher l'invitation à swiper (Voir plus ➡️)
                    </LabelAny>
                </div>
            </div>

            <TabsAny defaultValue="brands" className="w-full">
                <TabsListAny className="grid w-full grid-cols-2 mb-8 max-w-md">
                    <TabsTriggerAny value="brands">Affiches Marques</TabsTriggerAny>
                    <TabsTriggerAny value="header">Affiche Header (Partie X)</TabsTriggerAny>
                </TabsListAny>

                {/* ─── Header Tab ───────────────────────────────────────────── */}
                <TabsContentAny value="header" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Affiche de Série</CardTitle>
                            <CardDescription>
                                "Les marques indépendantes à connaître absolument."
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-end gap-4 max-w-md">
                                <div className="grid w-full gap-1.5">
                                    <LabelAny htmlFor="part">Texte de la pastille (ex: Partie 2, Spéciale...)</LabelAny>
                                    <InputAny
                                        id="part"
                                        type="text"
                                        value={headerPart}
                                        onChange={(e: any) => setHeaderPart(e.target.value)}
                                    />
                                </div>
                                <Button
                                    onClick={generateHeader}
                                    disabled={generating === 'header'}
                                    className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-sm"
                                >
                                    {generating === 'header' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                                    Générer
                                </Button>
                            </div>

                            {previews.header && (
                                <div className="mt-8 relative group max-w-2xl mx-auto">
                                    <img src={previews.header} alt="Preview" className="w-full rounded-xl shadow-2xl border border-gray-100" />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
                                        <Button onClick={() => downloadImage(previews.header, `pepite_partie_${headerPart}.png`)} variant="secondary">
                                            <Download className="mr-2 h-4 w-4" /> Télécharger PNG
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContentAny>

                {/* ─── Brands Tab ───────────────────────────────────────────── */}
                <TabsContentAny value="brands" className="space-y-6">
                    {/* Search bar + Stats */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <div className="relative flex-1 max-w-lg">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Rechercher une marque…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all dark:bg-gray-900 dark:border-gray-700 dark:text-white"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground whitespace-nowrap">
                            <span className="bg-gray-100 dark:bg-gray-800 px-3 py-1.5 rounded-md font-medium">
                                {filteredBrands.length} marque{filteredBrands.length > 1 ? 's' : ''}
                                {searchQuery && ` sur ${brands.length}`}
                            </span>
                            {totalPages > 1 && (
                                <span className="text-xs">
                                    Page {currentPage}/{totalPages}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* No results */}
                    {filteredBrands.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                            <Search className="h-12 w-12 text-gray-300" />
                            <p className="text-lg font-medium text-gray-500">Aucune marque trouvée</p>
                            <p className="text-sm text-muted-foreground">
                                Essayez un autre terme de recherche
                            </p>
                            <Button variant="ghost" size="sm" onClick={() => setSearchQuery('')}>
                                Réinitialiser la recherche
                            </Button>
                        </div>
                    )}

                    {/* Brand Cards Grid */}
                    {paginatedBrands.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {paginatedBrands.map(brand => (
                                <BrandCard
                                    key={brand.id}
                                    brand={brand}
                                    selectedPhotos={selectedPhotos[brand.id] || []}
                                    previews={previews[brand.id]}
                                    generating={generating}
                                    onSelectBrand={handleSelectBrand}
                                />
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </TabsContentAny>
            </TabsAny>

            {/* Brand Product Picker Modal */}
            {activeBrand && (
                <BrandProductPickerModal
                    brand={activeBrand}
                    selectedPhotos={selectedPhotos[activeBrand.id] || []}
                    previews={previews[activeBrand.id]}
                    generating={generating}
                    localDescription={localDescriptions[activeBrand.id]}
                    showNextArrow={showNextArrow}
                    onTogglePhoto={togglePhoto}
                    onGeneratePoster={generateBrandPoster}
                    onDownload={downloadImage}
                    onDescriptionChange={handleDescriptionChange}
                    onClose={() => setActiveBrand(null)}
                />
            )}
        </div>
    );
};

export default PosterStudio;
